import { useEffect, useRef, useState } from "react";
import { Lock } from "lucide-react";
import { useAccountStatus } from "../hooks/useAccountStatus";
import { clearProCheckoutIntent, readProCheckoutIntent, startProCheckout } from "../lib/proCheckout";
import "../pro-checkout.css";

// Picks up a Pro purchase that was interrupted by sign-in.
// Rendered on /account and in the members area (where the magic link and
// Google sign-in land). If the visitor clicked "Unlock Pro" while signed out,
// then signed in within 2 hours, this opens Stripe Checkout straight away.
// On /account while still signed out it shows a small "step 1 of 2" note so the
// visitor knows why they are signing in.
//
// Pass the page's existing useAccountStatus() result as `account` so this does
// not add a second Supabase query.
// Local GA call instead of importing trackEvent from App.jsx, which imports
// this file (avoids a circular import).
function trackEvent(name, params = {}) {
  if (typeof window !== "undefined" && typeof window.gtag === "function") window.gtag("event", name, params);
}

export default function ProCheckoutResume({ account, showSignInHint = false }) {
  const [intent] = useState(() => readProCheckoutIntent());
  const [status, setStatus] = useState("idle"); // idle | error
  const [error, setError] = useState("");
  const startedRef = useRef(false);

  useEffect(() => {
    if (!intent || startedRef.current || account?.loading) return;
    if (account?.isPro) {
      clearProCheckoutIntent();
      return;
    }
    if (!account?.session) return;

    startedRef.current = true;
    // Clear first so a failed or cancelled checkout can never loop.
    clearProCheckoutIntent();
    trackEvent("pro_checkout_resume", { source: intent.source || "pro" });
    startProCheckout(account.session).then((result) => {
      if (result.url) {
        window.location.href = result.url;
        return;
      }
      setStatus("error");
      setError(result.error);
    });
  }, [intent, account?.loading, account?.isPro, account?.session]);

  // Derived, not stored: the overlay shows while the effect above is starting
  // checkout (signed in, not Pro yet, no error).
  const redirecting = Boolean(intent) && status !== "error" && !account?.loading && Boolean(account?.session) && !account?.isPro;

  if (!intent && status === "idle") return null;

  if (redirecting) {
    return (
      <div className="pro-resume-overlay" role="status" aria-live="polite">
        <div className="pro-resume-card">
          <span className="pro-resume-spinner" aria-hidden="true" />
          <strong>Opening secure checkout…</strong>
          <span>You are signed in. Pro will unlock on this account after payment.</span>
        </div>
      </div>
    );
  }

  if (status === "error") {
    return (
      <div className="pro-resume-note is-error" role="alert">
        <strong>Checkout did not open.</strong>
        <span>{error} <a href="/account">Try again from your account</a>.</span>
      </div>
    );
  }

  if (showSignInHint && !account?.loading && !account?.session) {
    return (
      <div className="pro-resume-note" role="note">
        <Lock size={15} strokeWidth={2.5} aria-hidden="true" />
        <span>
          <strong>Step 1 of 2:</strong> sign in so Pro is saved to your account. Secure checkout opens right after.
        </span>
      </div>
    );
  }

  return null;
}

// For pages that do not already hold an account status (the /account route in
// App.jsx, where AccountPage owns its own hook). Render it only when an intent
// exists so ordinary /account visits add no extra Supabase query.
export function ProCheckoutResumeStandalone(props) {
  const account = useAccountStatus();
  return <ProCheckoutResume account={account} {...props} />;
}
